import { get } from 'lodash-es';

/**
 * 获取一行数据的分组 key。
 */
export function getGroupKey(data, field) {
  if (data == null || !field) {
    return undefined;
  }
  return get(data, field);
}

/**
 * 分组 key 是否为空值（null / undefined）。
 */
function isEmptyKey(key) {
  return key === null || key === undefined;
}

/**
 * 分组 key 是否视为“独立行”（不与相邻行合并）。
 * 默认空值（null / undefined）各自独立；当 mergeEmpty 为 true 时，空值也作为一个正常分组参与合并。
 */
export function isIndependentKey(key, mergeEmpty = false) {
  if (mergeEmpty) {
    return false;
  }
  return isEmptyKey(key);
}

/**
 * 把 key 归一化成可比较的字符串（仅用于非独立的 key）。
 */
function normalizeKey(key) {
  if (typeof key === 'object') {
    return JSON.stringify(key);
  }
  return String(key);
}

/**
 * 比较两个分组 key 是否属于同一个可合并的分组。
 * 默认任意一方为 null / undefined 都视为独立行，返回 false；
 * 当 mergeEmpty 为 true 时，空值之间互相合并（空与非空仍不合并）。
 */
function isSameGroupKey(keyA, keyB, mergeEmpty = false) {
  const aEmpty = isEmptyKey(keyA);
  const bEmpty = isEmptyKey(keyB);
  if (aEmpty || bEmpty) {
    if (!mergeEmpty) {
      return false;
    }
    return aEmpty && bEmpty;
  }
  return normalizeKey(keyA) === normalizeKey(keyB);
}

/**
 * 判断两个节点是否属于同一个可合并的分组。
 */
function isSameGroup(dataA, dataB, field, mergeEmpty) {
  return isSameGroupKey(getGroupKey(dataA, field), getGroupKey(dataB, field), mergeEmpty);
}

/**
 * ag-grid `spanRows` 的比较函数。
 * 原生的 `spanRows: true` 会把所有「值相等」的相邻单元格合并，而 null / undefined 之间互相相等，
 * 会导致所有独立行被错误地合并到一起。这里改用比较函数，默认任意一方为 null / undefined 都不合并，
 * 与选择逻辑（isSameGroup）保持一致。当列配置 notMergeEmptyValue 为 false 时，空值之间会合并。
 * 注意：valueA / valueB 是该列 colDef.field 对应的值，即分组 key 本身。
 */
export function spanRowsByGroupField(params) {
  const mergeEmpty = params.colDef?.notMergeEmptyValue === false;
  return isSameGroupKey(params.valueA, params.valueB, mergeEmpty);
}

/**
 * 判断某个节点是否是它所在分组（在当前过滤、显示状态下）的第一行。
 * 独立行（key 为 null / undefined 且未开启空值合并）永远视为自己组的第一行。
 */
function isFirstDisplayedRowOfGroup(api, node, field, mergeEmpty) {
  if (!node.data) {
    return false;
  }
  if (isIndependentKey(getGroupKey(node.data, field), mergeEmpty)) {
    return true;
  }
  const prev = api.getDisplayedRowAtIndex(node.rowIndex - 1);
  if (!prev || !prev.data) {
    return true;
  }
  return !isSameGroup(prev.data, node.data, field, mergeEmpty);
}

/**
 * 计算某个节点所在分组在当前显示顺序下的序号（从 0 开始），用于给相邻组做奇偶区分。
 * 做法：从第一行数到当前行，统计经过了多少个“组的第一行”。
 * 独立行（key 为 null / undefined 且未开启空值合并）各自算作一个组，序号也会递增。
 * 注意：这是从 index 0 开始的遍历，行数非常多时有一定开销。
 */
export function getGroupOrdinal(api, node, field, mergeEmpty) {
  if (!node.data) {
    return -1;
  }
  let ordinal = -1;
  for (let i = 0; i <= node.rowIndex; i += 1) {
    const n = api.getDisplayedRowAtIndex(i);
    if (n && n.data && isFirstDisplayedRowOfGroup(api, n, field, mergeEmpty)) {
      ordinal += 1;
    }
  }
  return ordinal;
}

/**
 * 获取某个节点所在分组在当前显示状态下的所有连续行节点。
 * 独立行（key 为 null / undefined 且未开启空值合并）只返回自身。
 */
export function getGroupRowNodes(api, node, field, mergeEmpty) {
  if (!node.data || isIndependentKey(getGroupKey(node.data, field), mergeEmpty)) {
    return [node];
  }

  // 找到分组在显示列表里的起始 index
  let startIndex = node.rowIndex;
  for (;;) {
    const prev = api.getDisplayedRowAtIndex(startIndex - 1);
    if (prev && prev.data && isSameGroup(prev.data, node.data, field, mergeEmpty)) {
      startIndex -= 1;
    } else {
      break;
    }
  }

  // 从起始 index 往下收集同组的连续节点
  const nodes = [];
  for (let i = startIndex; ; i += 1) {
    const n = api.getDisplayedRowAtIndex(i);
    if (n && n.data && isSameGroup(n.data, node.data, field, mergeEmpty)) {
      nodes.push(n);
    } else {
      break;
    }
  }

  return nodes;
}
